import {
  Autocomplete,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import Grid from "@mui/material/Grid2";
import SnoozeIcon from "@mui/icons-material/Snooze";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ActionFilterItems from "../ActionFilterItems/ActionFilterItems";
import SearchIcon from "@mui/icons-material/Search";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import SingleItemCreate from "../ActionItemsTableActions/SingleItemCreate/SingleItemCreate";
import { useEffect, useState } from "react";
import MultipleItemCreate from "../ActionItemsTableActions/MultipleItemCreate/MultipleItemCreate";
import AddToPhotosOutlinedIcon from "@mui/icons-material/AddToPhotosOutlined";
import { useDispatch } from "react-redux";
import { setSelectedActionItem } from "../../../../../features/actionItemsSlice";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { selectUserData } from "../../../../../features/userSlice";
import { CheckPermissions } from "../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";

interface ActionItemsFilterProps {
  onSnoozeIconClick: (snoozeIconType: string) => void;
  onAssignIconClick: (assignIconType: string) => void;
  onResolveIconClick: (resolveIconType: string) => void;
}
const ActionItemFilterCard = ({
  onSnoozeIconClick,
  onAssignIconClick,
  onResolveIconClick,
}: ActionItemsFilterProps) => {
  const dispatch = useDispatch();
  const { actionItemsList, selectedActionItem, selectedRows } = useSelector(
    (state: any) => state.actionItems
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const [openSingleCreate, setOpenSingleCreate] = useState(false);
  const [openMultipleCreate, setOpenMultipleCreate] = useState(false);

  //-- Get User Permissions for Action Items
  const response = useSelector(selectUserData);
  const hasCreateActionItemsPermission = CheckPermissions(
    response?.data?.products?.assets,
    "overview",
    "create_action_items"
  );
  const hasCreateMultipleActionItemsPermission = CheckPermissions(
    response?.data?.products?.assets,
    "overview",
    "create_multiple_action_items"
  );

  const hasEditMultipleActionItemsPermission = CheckPermissions(
    response?.data?.products?.assets,
    "overview",
    "edit_multiple_action_items"
  );

  //-- Clear selected action item
  const clearSelectedActionItems = () => {
    dispatch(setSelectedActionItem(null));
  };

  //open and close single popup
  const handleCloseSingle = () => {
    setOpenSingleCreate(false);
  };

  const handleOpenSingle = () => {
    setOpenSingleCreate(true);
  };

  //open and close multiple popup
  const handleCloseMultiple = () => {
    setOpenMultipleCreate(false);
  };

  const handleOpenMultiple = () => {
    setOpenMultipleCreate(true);
  };

  //-- this hold search params in url
  useEffect(() => {
    // Update selectedTemplate when searchParams change (e.g., on back/forward navigation)
    dispatch(setSelectedActionItem(searchParams.get("search")));
    // eslint-disable-next-line
  }, []);
  return (
    <>
      <Box>
        <Box>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid size={8}>
              <Typography variant="h3" sx={{ fontSize: "24px" }}>
                Action List
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontSize: "14px" }}
                color="rgba(0, 0, 0, 0.6)"
              >
                Organize Tasks, align objectives, hold stakeholders accountable
                and facilitate discussions around goals.
              </Typography>
            </Grid>
            <Grid
              size={4}
              container
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="flex-end"
            >
              <Grid>
                <Typography>Actions:</Typography>
              </Grid>
              {hasEditMultipleActionItemsPermission ? (
                <>
                  <Grid>
                    <Tooltip title="Snooze" placement="top">
                      <IconButton
                        aria-label="delete"
                        color="secondary"
                        disabled={selectedRows && selectedRows.length === 0}
                        onClick={() => onSnoozeIconClick("snooze")}
                      >
                        <SnoozeIcon
                          data-tracker="119"
                          color={
                            selectedRows.length > 0 ? "secondary" : "disabled"
                          }
                        />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid>
                    <Tooltip title="Assignee" placement="top">
                      <IconButton
                        aria-label="collection"
                        color="secondary"
                        disabled={selectedRows && selectedRows.length === 0}
                        onClick={() => onAssignIconClick("assign")}
                      >
                        <AddToPhotosOutlinedIcon
                          data-tracker="117"
                          color={
                            selectedRows.length > 0 ? "secondary" : "disabled"
                          }
                        />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid>
                    <Tooltip title="Resolve" placement="top">
                      <IconButton
                        aria-label="check"
                        disabled={selectedRows && selectedRows.length === 0}
                        onClick={() => onResolveIconClick("resolve")}
                      >
                        <CheckCircleOutlineOutlinedIcon
                          data-tracker="116"
                          color={
                            selectedRows.length > 0 ? "secondary" : "disabled"
                          }
                        />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid>
                    <Tooltip title="Snooze" placement="top">
                      <IconButton
                        aria-label="delete"
                        color="secondary"
                        disabled
                      >
                        <SnoozeIcon color={"disabled"} />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid>
                    <Tooltip title="Assignee" placement="top">
                      <IconButton
                        aria-label="collection"
                        color="secondary"
                        disabled
                      >
                        <AddToPhotosOutlinedIcon color={"disabled"} />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid>
                    <Tooltip title="Resolve" placement="top">
                      <IconButton aria-label="check" disabled>
                        <CheckCircleOutlineOutlinedIcon color={"disabled"} />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </Box>
        <Box>
          <Stack
            mt={0}
            mb={1}
            justifyContent={"space-between"}
            alignItems={"center"}
            direction="row"
            gap={2}
          >
            <Stack
              direction="row"
              spacing={2}
              sx={{
                overflowX: "auto",
                whiteSpace: "nowrap",
                display: "flex",
                "@media (max-width: 1800px)": {
                  width: "62%", // Change width to 100% when screen width >= 1400px
                },
                "@media (max-width: 1500px)": {
                  width: "58%", // Change width to 100% when screen width >= 1400px
                },
                "@media (max-width: 1400px)": {
                  width: "56%", // Change width to 100% when screen width >= 1400px
                },
                "@media (max-width: 1300px)": {
                  width: "49%", // Change width to 100% when screen width >= 1400px
                },
                "@media (max-width: 1200px)": {
                  width: "50%", // Change width to 100% when screen width >= 1400px
                },
                paddingBottom: "4px",
                /* Custom Scrollbar */
                "&::-webkit-scrollbar": {
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f0f0f0",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(189, 189, 189, 1)",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "rgba(192, 192, 192, 1)",
                },
              }}
            >
              <Box sx={{ flexShrink: 0, minWidth: "250px" }}>
                <Autocomplete
                  data-tracker="109"
                  sx={{ paddingTop: "8px" }}
                  disablePortal
                  options={Array.from(
                    new Set(
                      actionItemsList?.map(
                        (item: any) => item?.data["action_item"]
                      )
                    )
                  )}
                  onChange={(_, newValue) => {
                    dispatch(setSelectedActionItem(newValue));
                    setSearchParams((prevParams) => {
                      const updatedParams = Object.fromEntries(prevParams); // Preserve previous params
                      if (newValue) {
                        updatedParams.search = newValue; // Set new value
                        updatedParams.page = "0";
                      } else {
                        delete updatedParams.search; // Remove from URL if cleared
                      }
                      return updatedParams;
                    });
                  }}
                  value={selectedActionItem}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Action Item Name"
                      size="small"
                      color="secondary"
                      sx={{ width: "250px" }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ marginLeft: "12px" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              <Box sx={{ flexShrink: 0 }}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ paddingTop: "8px" }}
                >
                  <ActionFilterItems
                    clearSelectedActionItems={clearSelectedActionItems}
                  />
                </Stack>
              </Box>
            </Stack>
            <Stack>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                {hasCreateMultipleActionItemsPermission ? (
                  <Button
                    data-tracker="123"
                    variant="outlined"
                    color="secondary"
                    onClick={handleOpenMultiple}
                  >
                    Create multiple ITEMs
                  </Button>
                ) : (
                  <Button variant="outlined" color="secondary" disabled>
                    Create multiple ITEMs
                  </Button>
                )}

                {hasCreateActionItemsPermission ? (
                  <Button
                    data-tracker="121"
                    variant="contained"
                    color="secondary"
                  >
                    <Stack
                      direction="row"
                      alignItems={"initial"}
                      justifyContent={"center"}
                      gap={1}
                      onClick={handleOpenSingle}
                    >
                      Create single ITEM <AddOutlinedIcon fontSize="small" />
                    </Stack>
                  </Button>
                ) : (
                  <Button variant="contained" color="secondary" disabled>
                    <Stack
                      direction="row"
                      alignItems={"initial"}
                      justifyContent={"center"}
                      gap={1}
                    >
                      Create single ITEM <AddOutlinedIcon fontSize="small" />
                    </Stack>
                  </Button>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Box>
        {openSingleCreate ? (
          <SingleItemCreate
            open={openSingleCreate}
            handleClose={handleCloseSingle}
          />
        ) : (
          ""
        )}
        {openMultipleCreate ? (
          <MultipleItemCreate
            open={openMultipleCreate}
            handleClose={handleCloseMultiple}
          />
        ) : (
          ""
        )}
      </Box>
    </>
  );
};

export default ActionItemFilterCard;
